import React, { useEffect } from 'react';
import PropTypes from 'prop-types';

import EmailConfirmationModal from '@blocks/_common/email-confirmed-modal';
import SmallFallback from '@blocks/error-boundary/small-fallback';
import { createLogout, useSessionStore } from '@blocks/stores/session';
import withErrorBoundary from '@blocks/utils/withErrorBoundary';

import {
  AccountButton,
  AccountButtonPlug,
  AccountContainer,
  AccountEmptyButton,
  AccountEmptyButtonIcon,
  AccountEmptyButtonText,
  AccountLink,
  AccountLinkWithIcon,
  AccountList,
} from '../account/ui';
import {
  AUTH_EVENT_NAME,
  DOMAIN_DEFAULT,
  USER_ROLE_CUSTOMER,
  USER_ROLE_OPERATOR,
  USER_ROLE_SEO,
  USER_ROLE_WRITER,
} from '../constants';
import gaConfig from '../constants/ga-config';
import { Dropdown } from '../ui';
import { Myorders, Neworder, User } from '../ui/icon';
import { dispatchCustomEvent } from '../utils';
import { handleGaLogout, handleGaNewOrder } from '../utils/gtm/gtm-events';

import '../styles/global.css';
import '../styles/account.css';

const AccountMini = ({ domain = DOMAIN_DEFAULT, label = 'My account', link = '' }) => {
  const logoutHandler = createLogout({
    domain,
    onLogout: handleGaLogout(gaConfig),
  });

  const {
    session: { user_role, dashboard_home_url_path },
    customer,
    isFetching,
  } = useSessionStore(domain);

  useEffect(() => {
    if (!isFetching) {
      dispatchCustomEvent(AUTH_EVENT_NAME, { user_role });
    }
  }, [isFetching]);

  if (!user_role || (user_role === USER_ROLE_CUSTOMER && !customer.email)) {
    return (
      <>
        {link && (
          <AccountEmptyButton
            // @ts-ignore
            href={link}
            as="a"
          >
            <AccountEmptyButtonIcon>
              <User />
            </AccountEmptyButtonIcon>
            <AccountEmptyButtonText>{label}</AccountEmptyButtonText>
          </AccountEmptyButton>
        )}
        <EmailConfirmationModal />
      </>
    );
  }

  return (
    <AccountContainer>
      {customer.email && user_role === USER_ROLE_CUSTOMER && (
        <Dropdown
          button={active => (
            // @ts-ignore
            <AccountButton
              {...customer}
              active={active}
              aria-label="Account"
            />
          )}
        >
          <AccountList>
            <AccountLinkWithIcon
              href={`${dashboard_home_url_path}new_order/`}
              onClick={() => {
                handleGaNewOrder(gaConfig)(customer);
              }}
            >
              <Neworder /> New order
            </AccountLinkWithIcon>
            <AccountLink href={`${dashboard_home_url_path}orders/`}>Orders</AccountLink>
            <AccountLink href={`${dashboard_home_url_path}balance/`}>Balance</AccountLink>
            <AccountLink href={`${dashboard_home_url_path}ticketing/`}>Support</AccountLink>
            <AccountLink href={`${dashboard_home_url_path}profile/`}>Profile</AccountLink>
            <AccountLink onClick={logoutHandler}>Log out</AccountLink>
          </AccountList>
        </Dropdown>
      )}

      {user_role === USER_ROLE_WRITER && (
        <Dropdown
          button={active => (
            <AccountButtonPlug
              active={active}
              aria-label="Account"
            />
          )}
        >
          <AccountList>
            <AccountLinkWithIcon href={`${dashboard_home_url_path}orders_progress/`}>
              <Myorders /> My Orders
            </AccountLinkWithIcon>
            <AccountLink href={`${dashboard_home_url_path}profile/`}>
              Profile & Settings
            </AccountLink>
            <AccountLink href={`${dashboard_home_url_path}balance/`}>
              Financial Overview
            </AccountLink>
            <AccountLink onClick={logoutHandler}>Log out</AccountLink>
          </AccountList>
        </Dropdown>
      )}

      {user_role === USER_ROLE_OPERATOR && (
        <Dropdown
          button={active => (
            <AccountButtonPlug
              active={active}
              aria-label="Account"
            />
          )}
        >
          <AccountList>
            <AccountLinkWithIcon href={`${dashboard_home_url_path}`}>
              <Myorders /> Personal Area
            </AccountLinkWithIcon>
            <AccountLink href={`${dashboard_home_url_path}contract/`}>Support contract</AccountLink>
            <AccountLink onClick={logoutHandler}>Log out</AccountLink>
          </AccountList>
        </Dropdown>
      )}

      {user_role === USER_ROLE_SEO && (
        <Dropdown
          button={active => (
            <AccountButtonPlug
              active={active}
              aria-label="Account"
            />
          )}
        >
          <AccountList>
            <AccountLinkWithIcon href={`${dashboard_home_url_path}earnings/`}>
              <Myorders /> Earnings
            </AccountLinkWithIcon>
            <AccountLink href={`${dashboard_home_url_path}orders/`}>Orders</AccountLink>
            <AccountLink href={`${dashboard_home_url_path}customers/`}>Customers</AccountLink>
            <AccountLink onClick={logoutHandler}>Log out</AccountLink>
          </AccountList>
        </Dropdown>
      )}
    </AccountContainer>
  );
};

AccountMini.propTypes = {
  domain: PropTypes.string,
  label: PropTypes.string,
  link: PropTypes.string,
};

export default withErrorBoundary(AccountMini, <SmallFallback />);
